import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomModal from '../../util_components/custom_modal';
import Button from '../../util_components/button';
import { post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import GroupCancelFields from './group_cancel_fields';
import { GROUP_CANCEL_TEACHER } from '../sessions/single_session_details/session_change_rules';
import { group_cancel_payload, group_cancel_problem } from './group_cancel_rules';
import RequestTimeWarningStep from '../../util_components/request_time_warning';
import useRequestTimeWarning from '../../util_components/request_time_warning/use_request_time_warning';
import {
  client_request_time_warning,
  session_booked_at,
  with_request_time_ack
} from '../../util_components/request_time_warning/request_time_warning_rules';

// only a teacher pick sends a request time
const payload_has_time = (pick, requested_at) => pick === GROUP_CANCEL_TEACHER && !!requested_at;

const NO_ERRORS = { reason: false, who: false, time: false };
const ERROR_MESSAGE = 'Some error occured. Please contact dev team.';

// The group class / workshop Cancel pop-up; owns its form state so class_details.jsx stays small.
export default function GroupCancelModal({ show, close, session_details, on_cancelled, on_error }) {
  const [reason, set_reason] = useState('');
  const [pick, set_pick] = useState('');
  const [requested_at, set_requested_at] = useState('');
  const [errors, set_errors] = useState(NO_ERRORS);
  const [loading, set_loading] = useState(false);
  const rt = useRequestTimeWarning();
  const clear_warning = rt.clear;
  const booked_at = session_booked_at(session_details);

  useEffect(() => {
    if (!show) {
      set_reason('');
      set_pick('');
      set_requested_at('');
      set_errors(NO_ERRORS);
      clear_warning();
    }
  }, [show, clear_warning]);

  // clicking the picked option again clears it (no pick = apis keeps its default attribution)
  const on_pick = (option) => {
    set_pick(pick === option ? '' : option);
    set_requested_at('');
    set_errors(NO_ERRORS);
  };

  // ack: the request-time acknowledgement from the warning step (null on the first send)
  const submit = (ack = null) => {
    const problem = group_cancel_problem({
      reason,
      cancel_pick: pick,
      epoch_start_time: session_details.epoch_start_time,
      requested_local: requested_at,
      now_ms: Date.now(),
      session_type: session_details.session_type
    });
    if (problem) {
      set_errors({
        reason: problem === 'reason',
        who: problem === 'who',
        time: problem === 'time'
      });
      return;
    }
    const pre =
      !ack && payload_has_time(pick, requested_at)
        ? client_request_time_warning({
            requested_local: requested_at,
            class_start: session_details.epoch_start_time,
            booked_at
          })
        : null;
    if (pre) {
      rt.show(pre);
      return;
    }
    set_loading(true);
    const base = group_cancel_payload({
      session_uuid: session_details.session_uuid,
      reason,
      is_workshop: session_details.session_type === 'WORKSHOP_SESSION',
      cancel_pick: pick,
      requested_local: requested_at,
      session_type: session_details.session_type
    });
    const payload = with_request_time_ack(base, ack);
    post_api(get_api_url('cancel_group_session'), payload, true)
      .then(() => {
        set_loading(false);
        rt.clear();
        on_cancelled();
      })
      .catch((e) => {
        set_loading(false);
        if (rt.catch_warning(e)) return;
        on_error(e?.response?.data?.message || ERROR_MESSAGE);
      });
  };

  return (
    // form variant: the who-cancelled radios and the time picker use the shared form styling
    <CustomModal show={show} close={close} variant="form">
      {rt.warning ? (
        <RequestTimeWarningStep
          warning={rt.warning}
          requestedLocal={requested_at}
          busy={loading}
          onAcknowledge={(ack) => submit(ack)}
          onChangeTime={rt.clear}
        />
      ) : (
        <div>
          <div>
            <label htmlFor="group-cancel-reason">Enter Reason:</label>
            <textarea
              id="group-cancel-reason"
              aria-required="true"
              aria-invalid={errors.reason ? 'true' : undefined}
              aria-describedby={errors.reason ? 'group-cancel-reason-error' : undefined}
              name="cancel_reason"
              value={reason}
              onChange={(e) => e.target.value.length <= 200 && set_reason(e.target.value)}
              style={{ width: '300px', height: '100px' }}
            />
            <div style={{ fontSize: '10px' }}>* {200 - reason.length} characters left</div>
          </div>
          {/* the hidden copy keeps the layout steady; only a shown error is announced */}
          <p
            id="group-cancel-reason-error"
            role={errors.reason ? 'alert' : undefined}
            aria-hidden={errors.reason ? undefined : 'true'}
            style={errors.reason ? { color: 'var(--state-error)' } : { opacity: 0 }}
          >
            Please enter reason
          </p>
          <GroupCancelFields
            sessionType={session_details.session_type}
            startEpoch={session_details.epoch_start_time}
            pick={pick}
            requestedAt={requested_at}
            timeError={errors.time}
            whoErrorId={errors.who ? 'group-cancel-who-error' : ''}
            onPick={on_pick}
            bookedAt={booked_at}
            onRequestedAtChange={(value) => {
              set_requested_at(value);
              set_errors({ ...errors, time: false });
            }}
          />
          {errors.who ? (
            <p id="group-cancel-who-error" role="alert" style={{ color: 'var(--state-error)' }}>
              Please choose who cancelled
            </p>
          ) : null}
          <Button onClick={() => submit()} disabled={loading}>
            Confirm
          </Button>
        </div>
      )}
    </CustomModal>
  );
}

GroupCancelModal.propTypes = {
  show: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  session_details: PropTypes.object.isRequired,
  on_cancelled: PropTypes.func.isRequired,
  on_error: PropTypes.func.isRequired
};
